import { useCallback } from "react";
import { useDispatch } from "react-redux";
import type { FileAccessOption } from "@entrilia/shared/api/fileTypes";
import { isFileDownloadAllowed } from "@entrilia/shared/utilities/fileHelper";
import { logError } from "@entrilia/shared/logging";
import { api } from "../api/client";
import type { FileInfo } from "../api/types/commonApiTypes";
import { generateAttachmentUrlData } from "../services/attachments";
import { loadInboxStructure } from "../store/state/inboxStructure/thunks";
import { MessageActions } from "../store/state/messages/actions";
import type { Message, MessageLoadingState } from "../store/state/messages/types";
import type { AppDispatch } from "../store/store";

export interface PreviewDocument {
  fileInfo: FileInfo;
  preventDownload: boolean;
  // Re-mints the download URL when the SAS link expires; no read-status side effects.
  refresh: () => Promise<FileInfo | undefined>;
}

const buildRefresh =
  (messageId: string, attachmentId: string, forInvestorName?: string) => async (): Promise<FileInfo | undefined> => {
    try {
      return await generateAttachmentUrlData(messageId, attachmentId, forInvestorName);
    } catch (error) {
      logError(error, "[usePdfPreview] refresh attachment URL");
      return undefined;
    }
  };

export const usePdfPreview = () => {
  const dispatch: AppDispatch = useDispatch();

  const markMessageAsRead = useCallback(
    async (message: Message) => {
      if (!message.isRead) {
        try {
          const response = await api.messages.markAsRead(message.id);
          if (response.success && response.data.isRead === true) {
            dispatch(MessageActions.MarkAsRead(message.id));
            dispatch(loadInboxStructure());
          }
        } catch (error) {
          logError(error, "[usePdfPreview] markAsRead");
        }
      }
    },
    [dispatch]
  );

  const prepareMessageForPreview = useCallback(
    async (message: Message, updateReadStatus = true, forInvestorName?: string): Promise<PreviewDocument> => {
      dispatch(MessageActions.UpdateLoading({ markAsImportantUpdating: true } as MessageLoadingState));
      try {
        const detailsResp = await api.messages.getDetails(message.id);
        if (detailsResp.success && detailsResp.data?.attachments) {
          if (updateReadStatus) {
            void markMessageAsRead(message);
          }

          const attachment = detailsResp.data.attachments[0];
          if (attachment) {
            const downloadData = await generateAttachmentUrlData(message.id, attachment.id, forInvestorName);
            return {
              fileInfo: downloadData,
              preventDownload: !isFileDownloadAllowed(attachment.accessOptions),
              refresh: buildRefresh(message.id, attachment.id, forInvestorName),
            };
          }
        }

        throw new Error(detailsResp.error?.message ?? "Failed to get message details for PDF preview");
      } finally {
        dispatch(MessageActions.UpdateLoading({ markAsImportantUpdating: false } as MessageLoadingState));
      }
    },
    [dispatch, markMessageAsRead]
  );

  const prepareLinkForPreview = useCallback(
    async (
      messageId: string,
      attachmentId: string,
      forInvestorName?: string,
      accessOptions?: FileAccessOption[]
    ): Promise<PreviewDocument> => {
      const downloadData = await generateAttachmentUrlData(messageId, attachmentId, forInvestorName);
      return {
        fileInfo: downloadData,
        preventDownload: !isFileDownloadAllowed(accessOptions),
        refresh: buildRefresh(messageId, attachmentId, forInvestorName),
      };
    },
    []
  );

  return { prepareMessageForPreview, prepareLinkForPreview };
};
